import React, { useState, useEffect } from 'react';
import { publishAvatar } from '../layout/AppShell';

// Картки профілю, спільні для двох кабінетів: торгового (TradingAccountPage)
// і бектестингового (CalcAccountPage). Лише особисті дані акаунта —
// нічого про брокера чи бота.

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

/* ── Palette (Figma 303-35629) ─────────────────────────────────────── */
export const C = {
  card:      '#FFFFFF',
  border:    '#E5E7EB',
  textMain:  '#1A1A1A',
  textDim:   '#5B646F',
  primary:   '#475FFA',
  danger:    '#ED4B82',
  shadow:    '0px 2px 6px 0px rgba(0,0,0,0.04), 0px 0px 0px 1px rgba(0,0,0,0.06)',
};

/* ── Profile card: name / email / phone / avatar ───────────────────── */
export function ProfileCard({ user, onSaved }) {
  const [name,   setName]   = useState(user?.name  || '');
  const [email,  setEmail]  = useState(user?.email || '');
  const [phone,  setPhone]  = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  useEffect(() => {
    setName(user?.name || ''); setEmail(user?.email || ''); setPhone(user?.phone || '');
  }, [user?.name, user?.email, user?.phone]);

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/api/user/profile`, {
        method: 'PUT', headers,
        body: JSON.stringify({ name, phone, email }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setMsg({ type: 'error', text: typeof d.detail === 'string' ? d.detail : 'Помилка збереження' });
      } else {
        setMsg({ type: 'success', text: '✅ Збережено' });
        onSaved();
      }
    } catch {
      setMsg({ type: 'error', text: 'Помилка збереження' });
    }
    setSaving(false);
  };

  // Downscale the picked photo to ≤256px and store it as a small data URL.
  const onPickAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(url);
      const scale  = Math.min(1, 256 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.max(1, Math.round(img.width  * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      try {
        const r = await fetch(`${BASE}/api/user/avatar`, {
          method: 'POST', headers, body: JSON.stringify({ avatar: dataUrl }),
        });
        if (r.ok) { setMsg({ type: 'success', text: '✅ Фото оновлено' }); publishAvatar(dataUrl); onSaved(); }
        else setMsg({ type: 'error', text: 'Не вдалося зберегти фото' });
      } catch {
        setMsg({ type: 'error', text: 'Не вдалося зберегти фото' });
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); setMsg({ type: 'error', text: 'Не вдалося прочитати зображення' }); };
    img.src = url;
  };

  const removeAvatar = async () => {
    try {
      const r = await fetch(`${BASE}/api/user/avatar`, {
        method: 'POST', headers, body: JSON.stringify({ avatar: null }),
      });
      if (r.ok) { publishAvatar(null); onSaved(); }
    } catch { /* silent */ }
  };

  const FIELDS = [
    { label: "Ім'я",    value: name,  set: setName,  type: 'text'  },
    { label: 'Email',   value: email, set: setEmail, type: 'email' },
    { label: 'Телефон', value: phone, set: setPhone, type: 'tel'   },
  ];

  return (
    <div style={{
      background: C.card, border: `0.667px solid ${C.border}`,
      borderRadius: 20, padding: 24, boxShadow: C.shadow,
    }}>
      <div style={{ fontSize: 20, fontWeight: 600, color: C.textMain, marginBottom: 20, lineHeight: '28px' }}>
        Мої дані
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%', overflow: 'hidden',
            background: '#EFF2F5', border: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {user?.avatar
              ? <img src={user.avatar} alt="Аватар" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 34, color: '#9799A4' }}>👤</span>}
          </div>
          <label style={{
            padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
            background: C.card, fontSize: 13, cursor: 'pointer', color: C.textMain,
          }}>
            Завантажити фото
            <input type="file" accept="image/*" onChange={onPickAvatar} style={{ display: 'none' }} />
          </label>
          {user?.avatar && (
            <button
              onClick={removeAvatar}
              style={{ border: 'none', background: 'none', fontSize: 12, color: C.textDim, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              Прибрати фото
            </button>
          )}
        </div>

        {/* Fields */}
        <div style={{ flex: '1 1 320px', minWidth: 260 }}>
          {FIELDS.map(f => (
            <div key={f.label} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: C.textDim, display: 'block', marginBottom: 4 }}>{f.label}</label>
              <input
                type={f.type}
                value={f.value}
                onChange={e => f.set(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: `1px solid ${C.border}`, background: C.card,
                  fontSize: 14, outline: 'none', boxSizing: 'border-box', color: C.textMain,
                }}
              />
            </div>
          ))}
          {msg && (
            <div style={{
              padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
              background: msg.type === 'success' ? '#F0FDF4' : '#FEF2F2',
              color:      msg.type === 'success' ? '#15803D' : '#DC2626',
            }}>
              {msg.text}
            </div>
          )}
          <button
            onClick={save}
            disabled={saving}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: saving ? '#9CA3AF' : C.primary, color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? '⏳ Збереження…' : 'Зберегти'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Password card: change (or, for Google accounts, set) the password ── */
export function PasswordCard({ user, onSaved }) {
  const isGoogle = user?.auth_provider === 'google';
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [repeat,  setRepeat]  = useState('');
  const [show,    setShow]    = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  const save = async () => {
    setMsg(null);
    if (!isGoogle && !current) { setMsg({ type: 'error', text: 'Введіть поточний пароль' }); return; }
    if (next.length < 6)       { setMsg({ type: 'error', text: 'Новий пароль має бути не коротшим за 6 символів' }); return; }
    if (next !== repeat)       { setMsg({ type: 'error', text: 'Паролі не збігаються' }); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ current_password: isGoogle ? null : current, new_password: next }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setMsg({ type: 'error', text: typeof d.detail === 'string' ? d.detail : 'Не вдалося змінити пароль' });
      } else {
        setCurrent(''); setNext(''); setRepeat('');
        setMsg({ type: 'success', text: isGoogle ? '✅ Пароль встановлено — тепер можна входити і через email' : '✅ Пароль змінено' });
        onSaved && onSaved();
      }
    } catch {
      setMsg({ type: 'error', text: 'Не вдалося змінити пароль' });
    }
    setSaving(false);
  };

  const FIELDS = [
    ...(isGoogle ? [] : [{ label: 'Поточний пароль', value: current, set: setCurrent, ac: 'current-password' }]),
    { label: 'Новий пароль',          value: next,   set: setNext,   ac: 'new-password' },
    { label: 'Повторіть новий пароль', value: repeat, set: setRepeat, ac: 'new-password' },
  ];

  return (
    <div style={{
      background: C.card, border: `0.667px solid ${C.border}`,
      borderRadius: 20, padding: 24, boxShadow: C.shadow,
    }}>
      <div style={{ fontSize: 20, fontWeight: 600, color: C.textMain, marginBottom: 8, lineHeight: '28px' }}>
        {isGoogle ? 'Встановити пароль' : 'Змінити пароль'}
      </div>
      {isGoogle && (
        <div style={{ fontSize: 13, color: C.textDim, marginBottom: 16 }}>
          Ви входите через Google. Задайте пароль, щоб також входити за email і паролем.
        </div>
      )}
      <form
        onSubmit={e => { e.preventDefault(); if (!saving) save(); }}
        style={{ maxWidth: 420, marginTop: isGoogle ? 0 : 12 }}
      >
        {FIELDS.map(f => (
          <div key={f.label} style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: C.textDim, display: 'block', marginBottom: 4 }}>{f.label}</label>
            <input
              type={show ? 'text' : 'password'}
              value={f.value}
              autoComplete={f.ac}
              onChange={e => f.set(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: `1px solid ${C.border}`, background: C.card,
                fontSize: 14, outline: 'none', boxSizing: 'border-box', color: C.textMain,
              }}
            />
          </div>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textDim, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
          Показати паролі
        </label>
        {msg && (
          <div style={{
            padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: msg.type === 'success' ? '#F0FDF4' : '#FEF2F2',
            color:      msg.type === 'success' ? '#15803D' : '#DC2626',
          }}>
            {msg.text}
          </div>
        )}
        <button
          type="submit"
          disabled={saving}
          style={{
            padding: '10px 20px', borderRadius: 8, border: 'none',
            background: saving ? '#9CA3AF' : C.primary, color: '#fff',
            fontSize: 14, fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? '⏳ Збереження…' : (isGoogle ? 'Встановити пароль' : 'Змінити пароль')}
        </button>
      </form>
    </div>
  );
}
